import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

const fmt = (n) =>
  n == null ? '--' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (str) => {
  if (!str) return '--';
  return new Date(str + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtPct = (n) => {
  if (n == null) return null;
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
};

// ---------------------------------------------------------------------------
// Product row — expandable to show recent bills
// ---------------------------------------------------------------------------

const ProductRow = ({ product, index }) => {
  const [expanded, setExpanded] = useState(false);
  const drift = product.price?.drift_pct;
  const driftColor = drift == null ? 'text-zinc-400' : drift > 1 ? 'text-red-600' : drift < -1 ? 'text-emerald-600' : 'text-zinc-500';

  return (
    <>
      <tr
        onClick={() => setExpanded(!expanded)}
        className="group border-b border-zinc-50 cursor-pointer hover:bg-zinc-50/80 transition-colors"
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2 min-w-0">
            {product.item_number ? (
              <span className="text-xs font-mono text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded shrink-0">{product.item_number}</span>
            ) : (
              <span className="text-[10px] text-zinc-300 bg-zinc-50 px-1.5 py-0.5 rounded shrink-0">no sku</span>
            )}
            <span className="text-sm text-zinc-700 truncate">{product.description || '--'}</span>
          </div>
        </td>
        <td className="py-3 px-4 text-center">
          <span className="text-xs font-mono text-zinc-400 bg-zinc-50 px-2 py-0.5 rounded select-all">{product.product_hash}</span>
        </td>
        <td className="py-3 px-4 text-right">
          <span className="text-sm text-zinc-600 tabular-nums">{product.observations}</span>
        </td>
        <td className="py-3 px-4 text-right">
          <span className="text-sm text-zinc-800 font-medium tabular-nums">{fmt(product.price?.latest)}</span>
          {product.unit && <span className="text-[10px] text-zinc-400 ml-1">/{product.unit}</span>}
        </td>
        <td className="py-3 px-4 text-right">
          <span className="text-xs text-zinc-400 tabular-nums">{fmt(product.price?.min)}</span>
          <span className="text-zinc-300 mx-1">–</span>
          <span className="text-xs text-zinc-400 tabular-nums">{fmt(product.price?.max)}</span>
        </td>
        <td className="py-3 px-4 text-right">
          {drift != null ? (
            <span className={`text-sm font-medium tabular-nums ${driftColor}`}>{fmtPct(drift)}</span>
          ) : (
            <span className="text-xs text-zinc-300">--</span>
          )}
        </td>
        <td className="py-3 px-4 text-right">
          <span className="text-sm text-zinc-800 font-medium tabular-nums">{fmt(product.total_spend)}</span>
        </td>
        <td className="py-3 px-4 text-right text-xs text-zinc-400">
          {fmtDate(product.first_seen)}
        </td>
        <td className="pr-3 py-3 text-right">
          <svg
            width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
            className={`inline text-zinc-300 group-hover:text-zinc-400 transition-all ${expanded ? 'rotate-90' : ''}`}
          >
            <path d="M4.5 2.5l3 3-3 3" />
          </svg>
        </td>
      </tr>

      {/* Expanded — recent bills */}
      {expanded && product.recent_bills?.length > 0 && (
        <tr>
          <td colSpan={9} className="bg-zinc-50/50 px-4 py-3">
            <div className="ml-6">
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium mb-2">Recent appearances</p>
              <div className="space-y-1.5">
                {product.recent_bills.map((b, i) => (
                  <div key={i} className="flex items-center gap-4 text-xs">
                    <span className="text-zinc-500 w-24 shrink-0">{fmtDate(b.bill_date)}</span>
                    <span className="text-zinc-600 font-mono">{b.bill_number || '--'}</span>
                    <span className="text-zinc-500 tabular-nums ml-auto">{b.qty != null ? `${b.qty} ×` : ''} {fmt(b.unit_price)}</span>
                    <span className="text-zinc-700 font-medium tabular-nums w-24 text-right">{fmt(b.extended)}</span>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

const VendorProductsView = ({ loaded }) => {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('observations');
  const [sortDir, setSortDir] = useState('desc');

  // Fetch vendor list
  const fetchVendors = useCallback(async () => {
    try {
      const [vendorRes, statsRes] = await Promise.all([
        apiFetch('/api/vendors'),
        apiFetch('/api/vendors/stats'),
      ]);
      const vendorData = await vendorRes.json();
      const statsData = await statsRes.json();
      const merged = (vendorData.vendors || []).map(v => ({
        ...v,
        stats: statsData.stats?.[v.id] || {},
      }));
      setVendors(merged);
      // Auto-select first vendor
      if (merged.length > 0 && !selectedVendor) {
        setSelectedVendor(merged[0].id);
      }
    } catch {
      setError('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch products for selected vendor
  const fetchProducts = useCallback(async (vendorId) => {
    if (!vendorId) return;
    setProductsLoading(true);
    try {
      const res = await apiFetch(`/api/invoices-v2/vendors/${vendorId}/products`);
      if (!res.ok) throw new Error('Failed to load products');
      const data = await res.json();
      setProducts(data.products || []);
    } catch {
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => { if (loaded) fetchVendors(); }, [loaded, fetchVendors]);
  useEffect(() => { if (selectedVendor) fetchProducts(selectedVendor); }, [selectedVendor, fetchProducts]);

  const selectedVendorObj = vendors.find(v => v.id === selectedVendor);

  // Filter + sort
  const filtered = products.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.item_number || '').toLowerCase().includes(q)
      || (p.description || '').toLowerCase().includes(q)
      || (p.product_hash || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortCol) {
      case 'item': return ((a.item_number || a.description || '').localeCompare(b.item_number || b.description || '')) * dir;
      case 'observations': return (a.observations - b.observations) * dir;
      case 'price': return ((a.price?.latest || 0) - (b.price?.latest || 0)) * dir;
      case 'drift': return (Math.abs(a.price?.drift_pct || 0) - Math.abs(b.price?.drift_pct || 0)) * dir;
      case 'spend': return (a.total_spend - b.total_spend) * dir;
      default: return 0;
    }
  });

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  // Summary stats
  const driftItems = products.filter(p => p.price?.drift_pct != null);
  const increasing = driftItems.filter(p => p.price.drift_pct > 1).length;
  const decreasing = driftItems.filter(p => p.price.drift_pct < -1).length;
  const stable = driftItems.length - increasing - decreasing;

  const SortHeader = ({ col, label, align = 'left' }) => (
    <th
      onClick={() => handleSort(col)}
      className={`py-2.5 px-4 text-[10px] uppercase tracking-widest text-zinc-400 font-medium cursor-pointer hover:text-zinc-600 transition-colors text-${align} select-none`}
    >
      {label}
      {sortCol === col && <span className="ml-0.5">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );

  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-zinc-900">Vendor Products</h1>
        <p className="text-sm text-zinc-400 mt-0.5">Product registry by vendor — tracked via product hash</p>
      </div>

      {/* Vendor selector + search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={selectedVendor || ''}
          onChange={e => setSelectedVendor(e.target.value)}
          className="px-3 py-2 border border-zinc-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-300 min-w-[200px]"
        >
          {loading ? (
            <option>Loading vendors...</option>
          ) : (
            vendors.map(v => (
              <option key={v.id} value={v.id}>{v.display_name || v.company_name || v.id}</option>
            ))
          )}
        </select>

        <div className="relative sm:ml-auto">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full sm:w-72 pl-8 pr-3 py-2 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-zinc-300 bg-white"
          />
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
        </div>
      </div>

      {/* Stats row */}
      {selectedVendor && !productsLoading && products.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Products</p>
            <p className="text-2xl font-semibold text-zinc-900 mt-1 tabular-nums">{products.length}</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Price Increasing</p>
            <p className={`text-2xl font-semibold mt-1 tabular-nums ${increasing > 0 ? 'text-red-600' : 'text-zinc-900'}`}>{increasing}</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Price Decreasing</p>
            <p className={`text-2xl font-semibold mt-1 tabular-nums ${decreasing > 0 ? 'text-emerald-600' : 'text-zinc-900'}`}>{decreasing}</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-medium">Stable</p>
            <p className="text-2xl font-semibold text-zinc-900 mt-1 tabular-nums">{stable}</p>
          </div>
        </div>
      )}

      {/* Table */}
      {productsLoading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-16 text-red-500 text-sm">{error}</div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-sm text-zinc-400">
            {selectedVendor ? 'No products tracked for this vendor yet' : 'Select a vendor to view products'}
          </p>
          <p className="text-xs text-zinc-300 mt-1">Products appear here after invoices are processed</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-100 bg-zinc-50/50">
                <SortHeader col="item" label="Product" />
                <th className="py-2.5 px-4 text-[10px] uppercase tracking-widest text-zinc-400 font-medium text-center">Hash</th>
                <SortHeader col="observations" label="Seen" align="right" />
                <SortHeader col="price" label="Latest Price" align="right" />
                <th className="py-2.5 px-4 text-[10px] uppercase tracking-widest text-zinc-400 font-medium text-right">Range</th>
                <SortHeader col="drift" label="Drift" align="right" />
                <SortHeader col="spend" label="Total Spend" align="right" />
                <th className="py-2.5 px-4 text-[10px] uppercase tracking-widest text-zinc-400 font-medium text-right">First Seen</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => (
                <ProductRow key={p.product_hash} product={p} index={i} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VendorProductsView;
